import { DashboardContent } from 'src/layouts/dashboard';
import { useNotify, useTranslate } from 'react-admin';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { Checkbox, Tooltip, Stack, Grid, Box, Button, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, IconButton, Paper, FormControl, MenuItem, InputLabel, Select, SelectChangeEvent, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import AddIcon from '@mui/icons-material/Add';
import GetAppIcon from '@mui/icons-material/GetApp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useEffect, useState } from 'react';
import useRedeem from './useRedeem';
import { deleteRedeem, downloadRedeemCSV, generateRedeemCode } from 'src/pages/admin/services/whitelistService';
import TablePagination from '@mui/material/TablePagination';

export interface CouponCode {
  id: string;
  code: string;
  notes: string;
  maxUses: number | null;
  usedCount: number;
  createdAt: Date;
}

export interface RedeemSummary {
  totalCodes: number;
  cumulativeRedemptions: number;
  oneTimeCodes: number;
  unlimitedCodes: string;
}

const generateRandomCode = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = 'XBIT-WL-';
  for (let i = 0; i < 8; i += 1) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const View = () => {
  const translate = useTranslate();
  const notify = useNotify();

  const { retrieve, records: codes, total, retrieveSummary, summary } = useRedeem();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [maxUses, setMaxUses] = useState<number>(1);
  const [remark, setRemark] = useState('');
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const [disableTarget, setDisableTarget] = useState<CouponCode | null>(null);
  const [disabling, setDisabling] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    retrieve(page + 1, rowsPerPage);
    retrieveSummary();
  }, [page, rowsPerPage, retrieve, retrieveSummary]);

  const handleMaxUsesChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setMaxUses(parseInt(value, 10));
  };

  const handleGenerate = async () => {
    const newCodeStr = generateRandomCode();

    await generateRedeemCode(newCodeStr, remark, maxUses);

    setGeneratedCode(newCodeStr);
    setRemark('');

    retrieve(page + 1, rowsPerPage);
    retrieveSummary();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const exportToCSV = async () => {
    try {
      const response = await downloadRedeemCSV();

      const blob = new Blob([response.data], {
        type: 'text/csv;charset=utf-8;',
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', 'redeem.csv');

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to download CSV:', error);
    }
  };

  const getUsageLabel = (code: CouponCode) => {
    if (code.maxUses === null) {
      return `${code.usedCount}/不限`;
    }
    return `${code.usedCount}/${code.maxUses}`;
  };

  const formatDialogUsage = (code: CouponCode) => {
    if (code.maxUses != null && code.maxUses > 0) {
      return `${code.usedCount}/${code.maxUses}`;
    }
    return `${code.usedCount}/${translate('whitelist.redeem.unlimited')}`;
  };

  const handleDisableClose = () => {
    if (disabling) return;
    setDisableTarget(null);
  };

  const handleDisableConfirm = async () => {
    if (!disableTarget) return;
    setDisabling(true);
    try {
      const res = await deleteRedeem(disableTarget.id);
      setDisableTarget(null);
      if (res.data?.success === true) {
        notify('whitelist.redeem.disableSuccess', { type: 'success' });
        retrieve(page + 1, rowsPerPage);
        retrieveSummary();
      } else {
        notify('whitelist.redeem.disableFailed', { type: 'error' });
      }
    } catch (error: any) {
      setDisableTarget(null);
      const message = error?.response?.data?.message;
      if (message) {
        notify('%{_}', { type: 'error', messageArgs: { _: message } });
      } else {
        notify('whitelist.redeem.disableFailed', { type: 'error' });
      }
    } finally {
      setDisabling(false);
    }
  };

  return (
    <DashboardContent>
      <PermissionGuard resource="whitelist-redeem" action="read" fallback={<ErrorPage403 />}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="medium">
          {translate('whitelist.redeem.title')}
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2">
                  {translate('whitelist.redeem.totalAmount')}
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {summary ? summary.totalCodes : 0} {translate('whitelist.redeem.pieceUnit')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2">
                  {translate('whitelist.redeem.totalRedeemed')}
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {summary ? summary.cumulativeRedemptions : 0} {translate('whitelist.redeem.time')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2">
                  {translate('whitelist.redeem.oneTimeCode')}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  {summary ? summary.oneTimeCodes : 0} {translate('whitelist.redeem.pieceUnit')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2">
                  {translate('whitelist.redeem.unlimitedCode')}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {summary ? summary.unlimitedCodes : 0} {translate('whitelist.redeem.pieceUnit')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            {translate('whitelist.redeem.newRedeem')}
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={translate('whitelist.redeem.remark')}
                size="small"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder={translate('whitelist.redeem.remarkDesc')}
                autoComplete="off"
                spellCheck="false"
                inputProps={{
                  autoCapitalize: 'none',
                  autoCorrect: 'off',
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    height: 40,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{translate('whitelist.redeem.maxRedeem')}</InputLabel>
                <Select
                  value={maxUses === null ? 'unlimited' : maxUses.toString()}
                  label={translate('whitelist.redeem.maxRedeem')}
                  onChange={handleMaxUsesChange}
                  sx={{ height: 40 }}
                >
                  <MenuItem value="1">{translate('whitelist.redeem.oneTime')}</MenuItem>
                  <MenuItem value="10">10 {translate('whitelist.redeem.time')}</MenuItem>
                  <MenuItem value="50">50 {translate('whitelist.redeem.time')}</MenuItem>
                  <MenuItem value="100">100 {translate('whitelist.redeem.time')}</MenuItem>
                  <MenuItem value="500">500 {translate('whitelist.redeem.time')}</MenuItem>
                  <MenuItem value="0">{translate('whitelist.redeem.umlimited')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <PermissionGuard resource="whitelist-redeem" action="create" fallback={null}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleGenerate}
                  sx={{ height: 40 }}
                >
                  {translate('whitelist.redeem.generate')}
                </Button>
              </PermissionGuard>
            </Grid>
          </Grid>

          {generatedCode && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {translate('whitelist.redeem.newRedeemCode')}:
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1" fontFamily="monospace" fontWeight="bold">
                  {generatedCode}
                </Typography>
                <Tooltip
                  title={
                    copySuccess
                      ? translate('whitelist.redeem.copied')
                      : translate('whitelist.redeem.copy')
                  }
                >
                  <IconButton size="small" onClick={() => copyToClipboard(generatedCode)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          )}
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {translate('whitelist.redeem.allRedeemCodes')} ({codes.length})
          </Typography>
          <Stack direction="row" spacing={1}>
            <PermissionGuard resource="whitelist-redeem" action="export" fallback={null}>
              <Button
                variant="outlined"
                startIcon={<GetAppIcon />}
                onClick={exportToCSV}
                size="small"
              >
                {translate('whitelist.exportCSV')}
              </Button>
            </PermissionGuard>
          </Stack>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: '#fff',
                  '& th': {
                    backgroundColor: '#fff',
                    fontWeight: 600,
                  },
                }}
              >
                <TableCell>{translate('whitelist.redeem.menuTitle')}</TableCell>
                <TableCell>{translate('whitelist.redeem.remark')}</TableCell>
                <TableCell>{translate('whitelist.redeem.usageStatus')}</TableCell>
                <TableCell>{translate('whitelist.redeem.created')}</TableCell>
                <TableCell align="center">{translate('whitelist.redeem.action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {codes.map((code) => (
                <TableRow key={code.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{code.code}</TableCell>
                  <TableCell>{code.notes || '-'}</TableCell>
                  <TableCell>{getUsageLabel(code)}</TableCell>
                  <TableCell>{new Date(Number(code.createdAt)).toLocaleString()}</TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={
                        copySuccess
                          ? translate('whitelist.redeem.copied')
                          : translate('whitelist.redeem.copy')
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(code.code)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {code.usedCount === 0 && (
                    <PermissionGuard resource="whitelist-redeem" action="disable" fallback={null}>
                      <Tooltip title={translate('whitelist.redeem.remove')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={async () => {
                            setSelectedIds((prev) => {
                              const newSet = new Set(prev);
                              newSet.delete(code.id);
                              return newSet;
                            });

                            await deleteRedeem(code.id);
                            retrieve(page + 1, rowsPerPage);
                            retrieveSummary();
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </PermissionGuard>
                    )}
                    {code.usedCount > 0 && (
                      <PermissionGuard resource="whitelist-redeem" action="disable" fallback={null}>
                        <Tooltip title={translate('whitelist.redeem.disable')}>
                          <IconButton
                            size="small"
                            color="warning"
                            aria-label={translate('whitelist.redeem.disable')}
                            onClick={() => setDisableTarget(code)}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </PermissionGuard>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={summary ? summary.totalCodes : 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </TableContainer>
      </Box>
      <Dialog open={disableTarget !== null} onClose={handleDisableClose} maxWidth="xs" fullWidth>
        <DialogTitle>{translate('whitelist.redeem.disableTitle')}</DialogTitle>
        <DialogContent>
          {disableTarget && (
            <Stack spacing={1.5} sx={{ pt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {translate('whitelist.redeem.disableCode')}
                </Typography>
                <Typography variant="body1" fontFamily="monospace" fontWeight="bold">
                  {disableTarget.code}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {translate('whitelist.redeem.disableUsage')}
                </Typography>
                <Typography variant="body1">{formatDialogUsage(disableTarget)}</Typography>
              </Box>
              <Alert severity="warning">{translate('whitelist.redeem.disableWarning')}</Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDisableClose} disabled={disabling}>
            {translate('whitelist.redeem.cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleDisableConfirm}
            disabled={disabling}
          >
            {translate('whitelist.redeem.disable')}
          </Button>
        </DialogActions>
      </Dialog>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default View;
